import { PropertyFilterQuery } from "@amzn/awsui-collection-hooks";
import { useEffect, useState } from "react";

export interface UseServerSideDataParams {
  pagination?: {
    currentPageIndex?: number;
    pageSize?: number;
  };
  sorting?: {
    sortingColumn?: {
      sortingField?: string;
    };
    sortingDescending?: boolean;
  };
  filtering?: {
    filteringText?: string;
    filteringTokens?:
      | PropertyFilterQuery["tokens"]
      | PropertyFilterQuery["tokenGroups"];
    filteringOperation?: PropertyFilterQuery["operation"];
    additionalFilters?: { [key: string]: string };
  };
}

interface ServerSideDataResult<T> {
  items: T[];
  loading: boolean;
  totalCount: number;
  pagesCount: number;
  currentPageIndex: number;
  error?: any;
}

// Generic hook that takes a query hook as parameter
export function useServerSideData<T, P = any>(
  queryHook: (
    params: P,
    options?: { skip?: boolean; refetchOnMountOrArgChange?: boolean }
  ) => { data?: any; isLoading: boolean; error?: any },
  transformParams: (params: UseServerSideDataParams) => P,
  params: UseServerSideDataParams,
  options?: {
    defaultPageSize?: number;
    transformResponse?: (response: any) => { items: T[]; totalCount: number };
    skipQuery?: boolean;
  }
): ServerSideDataResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagesCount, setPagesCount] = useState(0);

  const shouldSkipQuery = options?.skipQuery || false;

  // Call the query hook with skip option
  const { data, isLoading, error } = queryHook(transformParams(params), {
    skip: shouldSkipQuery,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data) {
      const transformed = options?.transformResponse
        ? options.transformResponse(data)
        : { items: data.items, totalCount: data.totalCount };

      setItems(transformed.items);
      setTotalCount(transformed.totalCount);
      setPagesCount(
        Math.ceil(
          transformed.totalCount /
            (params.pagination?.pageSize || options?.defaultPageSize || 25)
        )
      );
    }
  }, [data]);

  return {
    items,
    loading: shouldSkipQuery ? false : isLoading,
    totalCount,
    pagesCount,
    currentPageIndex: params.pagination?.currentPageIndex || 1,
    error: shouldSkipQuery ? undefined : error,
  };
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
